import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/breathingBreak.css';

const PATTERNS = {
  box: {
    label: 'Box',
    tip: 'Equal sides — great for focus and stress relief.',
    phases: ['Inhale', 'Hold', 'Exhale', 'Hold'],
    durations: [4, 4, 4, 4],
  },
  '478': {
    label: '4-7-8',
    tip: 'Long hold and exhale calm the nervous system fast.',
    phases: ['Inhale', 'Hold', 'Exhale'],
    durations: [4, 7, 8],
  },
  calm: {
    label: 'Calm',
    tip: 'Gentle rhythm for winding down or light focus.',
    phases: ['Inhale', 'Hold', 'Exhale'],
    durations: [4, 2, 6],
  },
};

const DURATION_OPTIONS = [1, 2, 3, 4, 5];
const DEFAULT_DURATION = 2;
const DEFAULT_PATTERN = 'box';

const RING_RADIUS = 80;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ≈ 502.65

const PHASE_COLORS = {
  Inhale: '#818cf8',
  Hold: '#60a5fa',
  Exhale: '#34d399',
};

const CLOSING_MESSAGES = [
  'Your nervous system thanks you.',
  'That pause was time well spent.',
  'Carry that calm with you.',
];

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export default function BreathingBreak({ onClose }) {
  const [screen, setScreen] = useState('setup'); // setup | active | complete
  const [selectedDuration, setSelectedDuration] = useState(DEFAULT_DURATION);
  const [selectedPattern, setSelectedPattern] = useState(DEFAULT_PATTERN);

  // Active screen display state
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [phaseSecondsLeft, setPhaseSecondsLeft] = useState(0);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(0);
  const [ringOffset, setRingOffset] = useState(RING_CIRCUMFERENCE);
  const [ringTransition, setRingTransition] = useState(0);

  // Complete screen stats
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [breathsTaken, setBreathsTaken] = useState(0);

  // Mutable refs for interval counters (avoid stale closures)
  const intervalRef = useRef(null);
  const cyclesRef = useRef(0);
  const breathsRef = useRef(0);

  useEffect(() => {
    return () => clearInterval(intervalRef.current);
  }, []);

  const endSession = useCallback((cycles, breaths) => {
    clearInterval(intervalRef.current);
    setCyclesCompleted(cycles);
    setBreathsTaken(breaths);
    setScreen('complete');
  }, []);

  // Keep a stable ref to endSession for use inside the interval closure
  const endSessionRef = useRef(endSession);
  endSessionRef.current = endSession;

  const handleBegin = () => {
    const patternKey = selectedPattern;
    const pat = PATTERNS[patternKey];
    const totalSecs = selectedDuration * 60;
    const firstDur = pat.durations[0]; // always Inhale

    // Reset session counters
    cyclesRef.current = 0;
    breathsRef.current = 0;

    // Set display state — ring starts empty with no transition
    setPhaseIdx(0);
    setPhaseSecondsLeft(firstDur);
    setSessionSecondsLeft(totalSecs);
    setRingOffset(RING_CIRCUMFERENCE);
    setRingTransition(0);
    setScreen('active');

    // After React commits the active screen (ring at CIRCUMFERENCE), animate the fill.
    // Two rAF calls ensure the browser has painted at least one frame with the starting offset.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setRingOffset(0);
        setRingTransition(firstDur);
      });
    });

    // Mutable local counters for the interval — no stale-closure risk
    let phaseI = 0;
    let phaseTimer = firstDur;
    let sessionTimer = totalSecs;

    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      phaseTimer--;
      sessionTimer--;

      if (sessionTimer <= 0) {
        setSessionSecondsLeft(0);
        setPhaseSecondsLeft(0);
        endSessionRef.current(cyclesRef.current, breathsRef.current);
        return;
      }

      if (phaseTimer <= 0) {
        // Record completed phase
        if (pat.phases[phaseI] === 'Inhale') breathsRef.current++;

        // Advance to next phase
        phaseI = (phaseI + 1) % pat.phases.length;
        if (phaseI === 0) cyclesRef.current++;

        phaseTimer = pat.durations[phaseI];
        const nextPhase = pat.phases[phaseI];

        // Drive ring animation for the new phase
        if (nextPhase === 'Inhale') {
          setRingOffset(0);
          setRingTransition(phaseTimer);
        } else if (nextPhase === 'Exhale') {
          setRingOffset(RING_CIRCUMFERENCE);
          setRingTransition(phaseTimer);
        } else {
          // Hold — freeze ring, no transition
          setRingTransition(0);
        }

        setPhaseIdx(phaseI);
        setPhaseSecondsLeft(phaseTimer);
      } else {
        setPhaseSecondsLeft(phaseTimer);
      }

      setSessionSecondsLeft(sessionTimer);
    }, 1000);
  };

  const handleEndEarly = () => {
    clearInterval(intervalRef.current);
    setCyclesCompleted(cyclesRef.current);
    setBreathsTaken(breathsRef.current);
    setScreen('complete');
  };

  const handleAgain = () => {
    clearInterval(intervalRef.current);
    setRingOffset(RING_CIRCUMFERENCE);
    setRingTransition(0);
    setScreen('setup');
  };

  // Derived values for setup screen
  const pat = PATTERNS[selectedPattern];
  const cycleLength = pat.durations.reduce((a, b) => a + b, 0);
  const estimatedCycles = Math.floor((selectedDuration * 60) / cycleLength);

  // Derived values for active screen
  const currentPhaseName = screen === 'active'
    ? PATTERNS[selectedPattern].phases[phaseIdx]
    : 'Inhale';
  const ringColor = PHASE_COLORS[currentPhaseName];
  const isHoldPhase = currentPhaseName === 'Hold';

  return (
    <div
      className="bb-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Guided Breathing Break"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bb-modal">
        {/* Header */}
        <div className="bb-header">
          <div className="bb-title-row">
            <span className="bb-icon" aria-hidden="true">🌿</span>
            <h2 className="bb-title">Breathing Break</h2>
          </div>
          <button className="bb-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* ── Setup screen ───────────────────────────────────────────── */}
        {screen === 'setup' && (
          <div className="bb-setup">
            <div className="bb-section">
              <p className="bb-section-label">Duration</p>
              <div className="bb-pills">
                {DURATION_OPTIONS.map((d) => (
                  <button
                    key={d}
                    className={`bb-pill${selectedDuration === d ? ' bb-pill--active' : ''}`}
                    onClick={() => setSelectedDuration(d)}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            </div>

            <div className="bb-section">
              <p className="bb-section-label">Pattern</p>
              <div className="bb-pattern-grid">
                {Object.entries(PATTERNS).map(([key, p]) => (
                  <button
                    key={key}
                    className={`bb-pattern-card${selectedPattern === key ? ' bb-pattern-card--active' : ''}`}
                    onClick={() => setSelectedPattern(key)}
                  >
                    <span className="bb-pattern-name">{p.label}</span>
                    <span className="bb-pattern-ratio">{p.durations.join('-')}</span>
                  </button>
                ))}
              </div>
              <p className="bb-pattern-tip">{pat.tip}</p>
            </div>

            <p className="bb-summary">
              {selectedDuration} min · ~{estimatedCycles} cycles
            </p>

            <button className="bb-cta" onClick={handleBegin}>
              Begin
            </button>
          </div>
        )}

        {/* ── Active screen ──────────────────────────────────────────── */}
        {screen === 'active' && (
          <div className="bb-active">
            <div className="bb-ring-wrap">
              <svg
                viewBox="0 0 200 200"
                className="bb-ring-svg"
                aria-hidden="true"
              >
                {/* Background track */}
                <circle
                  cx="100" cy="100" r={RING_RADIUS}
                  fill="none"
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="9"
                />
                {/* Animated progress ring */}
                <circle
                  cx="100" cy="100" r={RING_RADIUS}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="9"
                  strokeLinecap="round"
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={ringOffset}
                  transform="rotate(-90 100 100)"
                  className={isHoldPhase ? 'bb-ring-hold' : ''}
                  style={{
                    transition: `stroke-dashoffset ${ringTransition}s linear, stroke 0.4s ease`,
                    filter: `drop-shadow(0 0 7px ${ringColor}99)`,
                  }}
                />
              </svg>

              {/* Centre label */}
              <div className="bb-ring-center" aria-live="polite" aria-atomic="true">
                <p className="bb-phase-name" style={{ color: ringColor }}>
                  {currentPhaseName}
                </p>
                <p className="bb-phase-count">{phaseSecondsLeft}</p>
              </div>
            </div>

            <p className="bb-session-time">
              {formatTime(sessionSecondsLeft)} remaining
            </p>

            <button className="bb-end-early" onClick={handleEndEarly}>
              End early
            </button>
          </div>
        )}

        {/* ── Complete screen ────────────────────────────────────────── */}
        {screen === 'complete' && (
          <div className="bb-complete">
            <div className="bb-complete-emoji">😌</div>
            <h3 className="bb-complete-heading">Well done</h3>
            <p className="bb-closing-msg">
              {CLOSING_MESSAGES[cyclesCompleted % CLOSING_MESSAGES.length]}
            </p>

            <div className="bb-stats">
              <div className="bb-stat">
                <span className="bb-stat-value">{cyclesCompleted}</span>
                <span className="bb-stat-label">cycles</span>
              </div>
              <div className="bb-stat">
                <span className="bb-stat-value">{selectedDuration}</span>
                <span className="bb-stat-label">minutes</span>
              </div>
              <div className="bb-stat">
                <span className="bb-stat-value">{breathsTaken}</span>
                <span className="bb-stat-label">breaths</span>
              </div>
            </div>

            <button className="bb-cta" onClick={handleAgain}>
              Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
